import { useCallback, useEffect, useRef, useState } from 'react';
import { loadSdk } from './RadarGmailDraft';
import { radarApi, type ContactCheck, type ContactLedgerState } from './radarApi';

const clientId=import.meta.env.VITE_RADAR_GOOGLE_CLIENT_ID as string|undefined;
// Background checks may use their own Google client; other Gmail features keep theirs.
const legacyBackgroundClientId=(import.meta.env.VITE_RADAR_GOOGLE_BACKGROUND_CLIENT_ID as string|undefined) || clientId;
const readonlyScope='https://www.googleapis.com/auth/gmail.readonly';
export const contactStatusLabels:Record<string,string>={drafted:'Draft in Gmail',abandoned:'Draft abandoned',sent:'Sent',replied:'Replied',bounced:'Bounced',suppressed:'Do not contact'};
const message=(e:unknown,fallback:string)=>e instanceof Error?e.message:fallback;
export const exportPaused='Gmail could not be fully checked for earlier sends and replies, so nothing was saved. Try again in a moment.';
// Saving to Gmail proceeds only after a finished check. Without a connected
// account there is no automatic check and the earlier manual behaviour is kept.
export function exportBlockReason(check:ContactCheck|undefined) {
  if(check?.reason==='reconnect_required')return 'Google no longer accepts the saved authorization, so nothing was saved. Reconnect Gmail in Contact status before saving drafts.';
  return check && (check.complete || check.source==='not_connected')?'':exportPaused;
}
type Item=ContactLedgerState['items'][number];
const when=(item:Item)=>item.suppressed_at||item.bounced_at||item.replied_at||item.sent_at||(item.status==='abandoned'?item.abandoned_at:null)||item.drafted_at;

// Contact facts for this workspace. With a connected Gmail account the server
// checks on its own each day and whenever this page opens; otherwise Gmail is read
// only after an explicit click with a short-lived token kept in memory. Nothing
// here sends or schedules mail.
export function RadarContactLedger({token,workspaceId,active=true,revision=0,onChanged,focusedCompany,focusRevision=0}:{
  token:string;workspaceId:string;active?:boolean;revision?:number;onChanged?:()=>void;focusedCompany?:string|null;focusRevision?:number;
}) {
  const focusedRef=useRef<HTMLElement>(null);
  const [data,setData]=useState<ContactLedgerState|null>(null);
  const serverClient=useRef<string>();
  const backgroundClientId=serverClient.current||legacyBackgroundClientId;
  const [busy,setBusy]=useState(false),[error,setError]=useState('');
  const authorization=useRef<{token:string;expiresAt:number}|null>(null);
  const run=useRef(0),alive=useRef(false),lock=useRef(false),lastSync=useRef(0);
  const read=useCallback(async(force=false)=>{
    if(!active || lock.current)return;
    const current=++run.current;lock.current=true;setBusy(true);setError('');
    try {
      let next=await radarApi.contactLedger(token,workspaceId);
      if(alive.current&&run.current===current&&next.client_id)serverClient.current=next.client_id;
      const auth=authorization.current;let synced=false;
      const connected=next.background?.connected && next.background.status==='active';
      const browser=auth && auth.expiresAt>Date.now();
      if(alive.current && run.current===current && next.enabled && (connected || browser)
          && (force || Date.now()-lastSync.current>60000)) {
        lastSync.current=Date.now();
        next=await radarApi.refreshContactLedger(token,workspaceId,connected?undefined:auth!.token);synced=true;
      }
      if(alive.current && run.current===current){setData(next);if(synced)onChanged?.();}
    }catch(err){if(alive.current && run.current===current)setError(message(err,'Contact status could not refresh.'));}
    finally{if(alive.current && run.current===current){lock.current=false;setBusy(false);}}
  },[token,workspaceId,active,onChanged]);

  useEffect(()=>{authorization.current=null;serverClient.current=undefined;lastSync.current=0;setData(null);},[token,workspaceId]);
  useEffect(()=>{
    alive.current=true;lock.current=false;
    if(active)void read();
    return()=>{alive.current=false;run.current++;};
  },[read,active,revision]);

  useEffect(()=>{if(active&&focusedCompany)focusedRef.current?.scrollIntoView?.({block:'nearest'});},[active,focusedCompany,focusRevision,data]);
  // One guarded server call that replaces the list and tells the batch view.
  async function change(call:()=>Promise<ContactLedgerState>,fallback:string) {
    if(lock.current)return;
    const current=++run.current;lock.current=true;setBusy(true);setError('');
    try {
      const next=await call();
      if(alive.current && run.current===current){setData(next);onChanged?.();}
    }catch(err){if(alive.current && run.current===current)setError(message(err,fallback));}
    finally{if(alive.current && run.current===current){lock.current=false;setBusy(false);}}
  }

  async function refresh() {
    if(lock.current)return;
    const connected=data?.background?.connected && data.background.status==='active';
    if(connected || (authorization.current && authorization.current.expiresAt>Date.now())){await read(true);return;}
    if(!clientId){await read();return;}
    const current=++run.current;lock.current=true;setBusy(true);setError('');
    const finish=()=>{if(alive.current && run.current===current){lock.current=false;setBusy(false);}};
    try {
      const sdk=await loadSdk();
      if(!alive.current || run.current!==current)return;
      sdk.accounts.oauth2.initTokenClient({client_id:clientId,scope:'openid email '+readonlyScope,
        error_callback:()=>{if(alive.current && run.current===current)setError('Gmail authorization was cancelled.');finish();},
        callback:response=>{
          if(!alive.current || run.current!==current)return;
          if(response.error || !response.access_token || !response.scope?.split(' ').includes(readonlyScope)){
            setError('Gmail read permission is required to check sent mail and replies. Draft export is unchanged.');finish();return;
          }
          authorization.current={token:response.access_token,expiresAt:Date.now()+((response.expires_in||3600)-60)*1000};
          finish();void read(true);
        }}).requestAccessToken({prompt:'select_account'});
    }catch(err){if(alive.current && run.current===current)setError(message(err,'Gmail authorization is unavailable.'));finish();}
  }

  // One-time consent for unattended checks. The one-time code goes straight to
  // the server; no Google token is kept in this page.
  async function connect() {
    if(lock.current || !backgroundClientId)return;
    const current=++run.current;lock.current=true;setBusy(true);setError('');
    const finish=()=>{if(alive.current && run.current===current){lock.current=false;setBusy(false);}};
    try {
      const sdk=await loadSdk();
      if(!alive.current || run.current!==current)return;
      if(!sdk.accounts.oauth2.initCodeClient)throw new Error('Gmail authorization is unavailable.');
      sdk.accounts.oauth2.initCodeClient({client_id:backgroundClientId,scope:'openid email '+readonlyScope,ux_mode:'popup',
        error_callback:()=>{if(alive.current && run.current===current)setError('Gmail authorization was cancelled.');finish();},
        callback:response=>{
          if(!alive.current || run.current!==current)return;
          if(response.error || !response.code){setError('Gmail read permission is required for automatic checks.');finish();return;}
          finish();
          void change(()=>radarApi.connectContactGmail(token,workspaceId,response.code!),'Could not connect Gmail.').then(()=>{lastSync.current=0;return read(true);});
        }}).requestCode();
    }catch(err){if(alive.current && run.current===current)setError(message(err,'Gmail authorization is unavailable.'));finish();}
  }

  if(!data?.enabled)return error?<p role="alert">{error}</p>:null;
  const background=data.background,connected=background?.connected && background.status==='active';
  return <section className="radar-outreach-ledger" aria-label="Contact status">
    <h2>Contact status</h2>
    <p className="radar-muted">Updated when you save drafts to Gmail and when Gmail is checked. Companies already contacted, held by a draft or marked do-not-contact are skipped in new batches.</p>
    {connected
      ? <p className="radar-muted">Automatic checks are on for <strong>{background!.account_email}</strong>: each day, when this page opens and before a batch is prepared.{background!.last_checked_at && <> Last daily check · <time dateTime={background!.last_checked_at}>{new Date(background!.last_checked_at).toLocaleString()}</time>.</>}</p>
      : background?.connected
        ? <p role="alert">Automatic checks stopped because Google no longer accepts the saved authorization for {background.account_email}. Connect Gmail again.</p>
        : <p className="radar-muted">Automatic checks are off. Connect Gmail once to keep this list current without coming back to check.</p>}
    <div className="radar-outreach-toolbar">
      <button className="radar-secondary-action" disabled={busy} onClick={()=>void refresh()}>{busy?'Checking…':connected?'Check now':'Check Gmail for sent mail and replies'}</button>
      {backgroundClientId && !connected && <button className="radar-secondary-action" disabled={busy} onClick={()=>void connect()}>Connect Gmail for automatic checks</button>}
      {background?.connected && <button className="radar-secondary-action" disabled={busy} onClick={()=>void change(()=>radarApi.disconnectContactGmail(token,workspaceId),'Could not disconnect Gmail.')}>Disconnect Gmail</button>}
    </div>
    {!clientId && <p className="radar-muted">Gmail connection is not configured yet.</p>}
    {data.check && !data.check.complete && data.check.source!=='not_connected' && <p role="status">{data.check.source==='check_failed'?'Gmail could not be checked just now.':'Not every contact was checked this time.'} Statuses below may be out of date; check again shortly.</p>}
    {error && <p role="alert">{error}</p>}
    {!data.items.length && <p className="radar-muted">No contacts recorded yet.</p>}
    {focusedCompany&&!data.items.some(item=>item.company_key===focusedCompany||(item.kind==='company'&&item.key===focusedCompany))&&<p role="status">This company is not in the loaded contact history. Its previous preparation may be outside the latest 20 batches.</p>}
    <div className="radar-outreach-ledger-list">{data.items.map(item=>{const at=when(item);const focused=item.company_key===focusedCompany||(item.kind==='company'&&item.key===focusedCompany);return <article key={item.id} ref={focused?focusedRef:undefined} data-focused={focused}>
      <div><strong>{item.company_name || item.company_key || item.key}</strong>
        <small>{item.kind==='company'?'Whole company':item.key}{item.origin==='import'?' · Imported history':''}</small></div>
      <p>{contactStatusLabels[item.status||''] || 'Recorded'}{at && <> · <time dateTime={at}>{new Date(at).toLocaleDateString()}</time></>}</p>
      {item.sync_error==='reconnect' && <small>Reconnect Gmail to check this contact.</small>}
      {item.sync_error==='check_failed' && <small>Could not check Gmail. Try again.</small>}
      <span className="radar-outreach-ledger-actions">
        {item.status==='drafted' && <button className="radar-secondary-action" disabled={busy} aria-label={`Abandon draft for ${item.key}`}
          title="Use after deleting the Gmail draft. The company can then be prepared again."
          onClick={()=>void change(()=>radarApi.abandonContactDraft(token,workspaceId,item.id),'Could not abandon this draft.')}>Abandon this draft</button>}
        {item.suppressed_at
          ? <button className="radar-secondary-action" disabled={busy} aria-label={`Allow contact with ${item.key}`} onClick={()=>void change(()=>radarApi.setContactSuppression(token,workspaceId,{kind:item.kind,key:item.key,reason:'manual',suppressed:false}),'Could not update this contact.')}>Allow contact again</button>
          : <button className="radar-secondary-action" disabled={busy} aria-label={`Do not contact ${item.key}`} onClick={()=>void change(()=>radarApi.setContactSuppression(token,workspaceId,{kind:item.kind,key:item.key,reason:'manual',suppressed:true}),'Could not update this contact.')}>Do not contact</button>}
      </span>
    </article>;})}</div>
  </section>;
}
